<template>
  <main class="employee-profile-page" :aria-busy="loading">
    <div class="profile-shell">
      <header class="profile-header">
        <img class="profile-logo" src="/assets/nawadi-logo.png" alt="Al Nawadi Holding" />
        <span>BUSINESS CARD</span>
      </header>

      <div v-if="loading" class="profile-state" role="status">
        <span class="loading-indicator" aria-hidden="true"></span>
        <h1>Loading profile</h1>
        <p>Getting contact details...</p>
      </div>
      <div v-else-if="error" class="profile-state" role="alert">
        <i class="bx bx-error-circle state-icon" aria-hidden="true"></i>
        <h1>Profile unavailable</h1>
        <p>We couldn’t load this employee’s details. Please try again.</p>
        <button type="button" class="retry-button" @click="fetchEmployee">Try again</button>
      </div>

      <section v-else class="business-card" aria-labelledby="employee-name">
        <div class="profile-intro">
          <img v-if="employee.image && !photoFailed" class="profile-photo" :src="employee.image" :alt="employee.name" @error="photoFailed = true" />
          <div v-else class="profile-photo profile-initials" aria-hidden="true">{{ initials }}</div>
          <h1 id="employee-name">{{ employee.name }}</h1>
          <p v-if="employee.position" class="profile-position">{{ employee.position }}</p>
          <p class="profile-company">Al Nawadi Holding</p>
          <div v-if="employee.mobile || employee.telephone || employee.email" class="quick-actions" aria-label="Quick contact actions">
            <a v-if="employee.mobile || employee.telephone" :href="phoneLink(employee.mobile || employee.telephone)">
              <i class="bx bx-phone" aria-hidden="true"></i> Call
            </a>
            <a v-if="employee.email" :href="'mailto:' + employee.email">
              <i class="bx bx-envelope" aria-hidden="true"></i> Email
            </a>
          </div>
        </div>

        <section v-if="contactRows.length" class="contact-section" aria-labelledby="contact-heading">
          <h2 id="contact-heading" class="contact-heading">Contact details</h2>
          <ul class="profile-contact">
            <li v-for="row in contactRows" :key="row.label">
              <component :is="row.href ? 'a' : 'div'" class="contact-row" :href="row.href || null" :target="row.external ? '_blank' : null" :rel="row.external ? 'noopener noreferrer' : null">
                <span class="contact-icon"><i :class="['bx', row.icon]" aria-hidden="true"></i></span>
                <span class="contact-copy">
                  <span class="contact-label">{{ row.label }}<span v-if="row.external" class="sr-only"> (opens in a new tab)</span></span>
                  <span class="contact-value">{{ row.value }}</span>
                </span>
                <i v-if="row.href" :class="['bx', row.external ? 'bx-link-external' : 'bx-chevron-right', 'contact-chevron']" aria-hidden="true"></i>
              </component>
            </li>
          </ul>
        </section>
        <div class="profile-actions">
          <button type="button" class="save-contact-button" @click="saveContact">
            <i class="bx bx-user-plus" aria-hidden="true"></i> Save contact
          </button>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import axios from "../axios";

export default {
  name: "EmployeeProfile",
  data() {
    return {
      employee: null,
      loading: true,
      error: false,
      photoFailed: false,
    };
  },
  computed: {
    initials() {
      return (this.employee.name || "Employee").trim().split(/\s+/).slice(0, 2).map(part => part[0]).join("");
    },
    contactRows() {
      const employee = this.employee;
      return [
        { label: "Mobile", value: employee.mobile, icon: "bx-mobile-alt", href: this.phoneLink(employee.mobile) },
        { label: "Email", value: employee.email, icon: "bx-envelope", href: employee.email ? "mailto:" + employee.email : null },
        { label: "Telephone", value: employee.telephone, icon: "bx-phone", href: this.phoneLink(employee.telephone) },
        { label: "Extension", value: employee.extension, icon: "bx-hash" },
        { label: "Website", value: employee.websiteLabel || employee.website, icon: "bx-globe", href: employee.website, external: true },
        { label: "Address", value: employee.address, icon: "bx-map" },
      ].filter(row => row.value !== null && row.value !== undefined && row.value !== "");
    },
  },
  methods: {
    phoneLink(value) {
      return value ? "tel:" + String(value).replace(/\s/g, "") : null;
    },
    fetchEmployee() {
      this.loading = true;
      this.error = false;
      this.photoFailed = false;
      const slug = this.$route.params.slug;

      axios.get(`/employees/${slug}`)
        .then((response) => {
          this.employee = response.data.data;
          if (!this.employee) this.error = true;
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveContact() {
      const employee = this.employee;
      const escapeVCardValue = (value) => String(value == null ? "" : value)
        .replace(/\\/g, "\\\\")
        .replace(/;/g, "\\;")
        .replace(/,/g, "\\,")
        .replace(/\r\n|\r|\n/g, "\\n");
      // The API supplies one full name. Keep it intact in the given-name
      // component; export both structured and display names for mobile importers.
      const name = escapeVCardValue(employee.name);
      const hasValue = (value) => value != null && String(value).trim() !== "";
      const optionalField = (property, value) => hasValue(value)
        ? `${property}:${escapeVCardValue(value)}` : null;
      const vCard = [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `N:;${name};;;`,
        `FN:${name}`,
        `ORG:${escapeVCardValue("Al Nawadi Holding")}`,
        optionalField("TITLE", employee.position),
        optionalField("TEL;TYPE=CELL", employee.mobile),
        optionalField("TEL;TYPE=WORK", employee.telephone),
        // An extension alone is not a dialable number. Preserve it as a note.
        hasValue(employee.extension) ? `NOTE:${escapeVCardValue("Work extension: " + employee.extension)}` : null,
        optionalField("EMAIL;TYPE=INTERNET,WORK", employee.email),
        optionalField("URL", employee.website),
        hasValue(employee.address) ? `ADR;TYPE=WORK:;;${escapeVCardValue(employee.address)};;;;` : null,
        "END:VCARD",
      ].filter(line => line !== null).join("\r\n") + "\r\n";
      const blob = new Blob([vCard], { type: "text/vcard;charset=utf-8" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${this.$route.params.slug || "employee"}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
    },
  },
  created() {
    this.fetchEmployee();
  },
  watch: {
    "$route.params.slug": "fetchEmployee",
  },
};
</script>

<style scoped>
.employee-profile-page {
  min-height: 100vh;
  min-height: 100dvh;
  padding: 32px 24px;
  background: #f5f3ef;
  color: #243142;
}
.employee-profile-page * { box-sizing: border-box; }
.profile-shell {
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e9e5df;
  border-radius: 24px;
  box-shadow: 0 12px 40px rgba(36, 49, 66, .06);
  overflow: hidden;
}
.profile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid #f0ece6;
}
.profile-logo { width: 65px; height: 54px; object-fit: contain; }
.profile-header span { color: #657080; font-size: 13px; font-weight: 500; }
.profile-intro { padding: 28px 24px 24px; text-align: center; background: linear-gradient(#fffbf4, #fff); }
.profile-photo {
  display: block;
  width: 112px;
  height: 112px;
  margin: 0 auto 18px;
  border: 4px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #eadcc4;
  object-fit: cover;
  object-position: center top;
}
.profile-initials { display: flex; align-items: center; justify-content: center; background: #f9ecd6; color: #87530e; font-size: 32px; font-weight: 700; }
.profile-intro h1 { margin: 0 0 8px; color: #243142; font-size: 26px; font-weight: 700; line-height: 1.25; letter-spacing: -.025em; overflow-wrap: anywhere; }
.profile-position { margin: 0 0 5px; color: #566273; font-size: 16px; line-height: 1.5; overflow-wrap: anywhere; }
.profile-company { margin: 0; color: #73716c; font-size: 13px; }
.quick-actions { display: flex; gap: 12px; margin-top: 24px; }
.quick-actions a { display: flex; flex: 1; align-items: center; justify-content: center; gap: 8px; min-height: 48px; padding: 12px; border: 1px solid #e8dfd1; border-radius: 12px; color: #805111; background: #fffaf2; font-size: 15px; font-weight: 600; text-decoration: none; }
.quick-actions i { font-size: 20px; }
.contact-section { padding: 0 24px 12px; }
.contact-heading { margin: 0; padding: 8px 0 12px; color: #667080; font-size: 13px; font-weight: 600; line-height: 1.5; }
.profile-contact { margin: 0; padding: 0; list-style: none; border: 1px solid #eceef0; border-radius: 16px; overflow: hidden; }
.profile-contact li + li { border-top: 1px solid #eceef0; }
.contact-row { display: flex; align-items: center; gap: 12px; min-height: 76px; padding: 14px; color: #243142; text-decoration: none; }
.contact-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 36px; height: 36px; border-radius: 10px; background: #faf4e9; color: #956017; font-size: 21px; }
.contact-copy { display: block; flex: 1; min-width: 0; }
.contact-label { display: block; margin-bottom: 3px; color: #687384; font-size: 12px; line-height: 1.4; }
.contact-value { display: block; color: #243142; font-size: 15px; font-weight: 500; line-height: 1.5; overflow-wrap: anywhere; }
.contact-chevron { flex-shrink: 0; color: #828a96; font-size: 18px; }
.profile-actions { padding: 16px 24px 24px; background: #fff; }
.save-contact-button, .retry-button { display: flex; align-items: center; justify-content: center; gap: 9px; min-height: 52px; width: 100%; padding: 14px 20px; border: 0; border-radius: 12px; background: #e49a2f; color: #30220e; cursor: pointer; font-family: inherit; font-size: 16px; font-weight: 700; line-height: 1.5; }
.save-contact-button i { font-size: 22px; }
a:focus-visible, button:focus-visible { outline: 3px solid #87530e; outline-offset: -3px; }
a:active, button:active { filter: brightness(.95); }
.profile-state { padding: 56px 24px; text-align: center; }
.profile-state h1 { margin: 20px 0 8px; font-size: 22px; color: #243142; }
.profile-state p { color: #687384; font-size: 15px; }
.retry-button { margin-top: 24px; }
.state-icon { color: #956017; font-size: 36px; }
.loading-indicator { display: inline-block; width: 32px; height: 32px; border: 3px solid #f6e8d2; border-top-color: #956017; border-radius: 50%; animation: profile-spin 1s linear infinite; }
@keyframes profile-spin { to { transform: rotate(360deg); } }
@media (hover: hover) {
  .contact-row[href]:hover { background: #fffbf4; }
  .quick-actions a:hover { background: #f9ecd6; }
  .save-contact-button:hover, .retry-button:hover { background: #d98c23; }
}
@media (max-width: 600px) {
  .employee-profile-page { padding: 0; background: #fff; }
  .profile-shell { max-width: none; border: 0; border-radius: 0; box-shadow: none; }
  .profile-header { padding-top: max(12px, env(safe-area-inset-top)); padding-left: max(20px, env(safe-area-inset-left)); padding-right: max(20px, env(safe-area-inset-right)); }
  .business-card { padding-bottom: calc(100px + env(safe-area-inset-bottom)); }
  .profile-intro { padding: 24px max(20px, env(safe-area-inset-left)) 24px max(20px, env(safe-area-inset-right)); }
  .contact-section { padding: 0 max(20px, env(safe-area-inset-right)) 12px max(20px, env(safe-area-inset-left)); }
  .profile-actions { position: fixed; bottom: 0; left: 0; right: 0; z-index: 10; padding: 12px max(20px, env(safe-area-inset-right)) calc(12px + env(safe-area-inset-bottom)) max(20px, env(safe-area-inset-left)); border-top: 1px solid #eceef0; }
}
@media (prefers-reduced-motion: reduce) { .loading-indicator { animation: none; } }
</style>
